import React from "react";
import { motion } from "framer-motion";
import { agentClients } from "../../Data/siteContent";

/* What VistaBridge actually is, in one picture:
   the assistant your team already uses  ->  an MCP server we build
   ->  the systems that hold your real numbers.

   Deliberately not a screenshot: there is no UI to show, and the whole
   objection to answer here is "where does my data go", which a diagram
   answers better than a dashboard would. */

const SYSTEMS = ["SAP", "Oracle", "SQL Server", "CollaBiz", "Your API"];

const ease = [0.21, 0.8, 0.25, 1];

export default function BridgeDiagram() {
  return (
    <div className="cv-bridge" role="img"
      aria-label="Diagram: Claude, ChatGPT, Copilot and IDE agents connect through an MCP server built by CafeVista to SAP, Oracle, SQL Server, CollaBiz or your own API. The connection is read-only by default and writes require approval.">
      {/* row 1 — the agents */}
      <div className="cv-bridge__row" aria-hidden="true">
        {agentClients.map((a, i) => (
          <motion.span
            key={a.id}
            className="cv-bridge__node"
            initial={{ opacity: 0, y: -12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.07, ease }}
          >
            <b>{a.mark}</b>
            <em>{a.note}</em>
          </motion.span>
        ))}
      </div>

      <Wires flip />

      {/* row 2 — the bridge itself */}
      <motion.div
        className="cv-bridge__core"
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2, ease }}
        aria-hidden="true"
      >
        <span className="cv-bridge__core-tag">MCP server</span>
        <strong>VistaBridge</strong>
        <span className="cv-bridge__core-note">built by CafeVista &middot; runs where you choose</span>
        <div className="cv-bridge__guards">
          <span>Read-only by default</span>
          <span>Scoped to named tables</span>
          <span>Writes need approval</span>
        </div>
      </motion.div>

      <Wires />

      {/* row 3 — the systems */}
      <div className="cv-bridge__row cv-bridge__row--sys" aria-hidden="true">
        {SYSTEMS.map((sname, i) => (
          <motion.span
            key={sname}
            className="cv-bridge__node cv-bridge__node--sys"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.3 + i * 0.06, ease }}
          >
            {sname}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

/* Connecting lines with a pulse travelling along them.

   Uses a travelling stroke-dash rather than offsetPath/offsetDistance: the
   motion-path properties need offsetDistance animated to move at all, and
   support is patchier than a dash offset, which works everywhere. */
function Wires({ flip }) {
  const xs = [70, 160, 240, 330];
  return (
    <svg
      className={"cv-bridge__wires" + (flip ? " is-flip" : "")}
      viewBox="0 0 400 60"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {xs.map((x, i) => {
        const d = `M${x} 0 C ${x} 30, 200 30, 200 60`;
        return (
          <g key={x}>
            <path d={d} stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.3" fill="none" />
            <path
              className="cv-bridge__pulse"
              d={d}
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              style={{ animationDelay: i * 0.5 + "s" }}
            />
          </g>
        );
      })}
    </svg>
  );
}
